/**
 * Phone Data Store with Supabase Integration
 *
 * This store manages all phone number data using Supabase.
 * Includes comprehensive logging, error handling, and real-time subscriptions.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { supabase } from '@/lib/supabase';
import { Logger, LogCategory, logDataOperation, logStateChange } from '@/utils/logger';
import { tryCatch, AppError, ErrorCode, ValidationError } from '@/utils/errorHandler';
import { PAGINATION_CONFIG } from '@/constants';
import type {
  PhoneNumber,
  PhoneActivity,
  PhoneCheckResult,
  DeviceCloudResult,
  PhoneFilters,
  PhoneSortConfig,
  PaginationState,
  FlagStatus,
  CallGroup,
} from '@/types';
import type { RealtimeChannel } from '@supabase/supabase-js';

// ============================================================================
// Types
// ============================================================================

interface PhoneState {
  // Data
  phones: PhoneNumber[];
  selectedPhone: PhoneNumber | null;
  selectedPhoneActivities: PhoneActivity[];
  selectedPhoneCheckResults: PhoneCheckResult[];
  selectedPhoneDeviceCloud: DeviceCloudResult[];
  callGroups: CallGroup[];

  // Filtering & Sorting
  filters: PhoneFilters;
  sortConfig: PhoneSortConfig;
  pagination: PaginationState;

  // UI State
  isLoading: boolean;
  isLoadingDetails: boolean;
  isSaving: boolean;
  error: string | null;
  selectedPhoneIds: string[];

  // Real-time subscription
  realtimeChannel: RealtimeChannel | null;

  // Actions
  fetchPhones: () => Promise<void>;
  fetchCallGroups: () => Promise<void>;
  fetchPhoneDetails: (id: string) => Promise<void>;
  updatePhone: (id: string, updates: Partial<PhoneNumber>) => Promise<void>;
  bulkUpdatePhones: (ids: string[], updates: Partial<PhoneNumber>) => Promise<void>;
  addPhones: (phones: Partial<PhoneNumber>[]) => Promise<void>;
  archivePhone: (id: string) => Promise<void>;
  bulkArchivePhones: (ids: string[]) => Promise<void>;
  subscribeToChanges: (companyId: string) => void;
  unsubscribeFromChanges: () => void;

  // Filtering & Sorting Actions
  setFilters: (filters: Partial<PhoneFilters>) => void;
  resetFilters: () => void;
  setSortConfig: (config: PhoneSortConfig) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;

  // Selection Actions
  selectPhone: (id: string) => void;
  deselectPhone: (id: string) => void;
  selectAllPhones: () => void;
  deselectAllPhones: () => void;
  togglePhoneSelection: (id: string) => void;

  // Utility Actions
  clearError: () => void;
  clearSelectedPhone: () => void;
  getFilteredPhones: () => PhoneNumber[];
  getPaginatedPhones: () => PhoneNumber[];
}

// ============================================================================
// Default Values
// ============================================================================

const defaultFilters: PhoneFilters = {
  search: '',
  status: 'all',
  phoneNumberGroup: 'all',
  dateRange: {
    start: null,
    end: null,
  },
};

const defaultSortConfig: PhoneSortConfig = {
  field: 'lastFlaggedDate',
  direction: 'desc',
};

const defaultPagination: PaginationState = {
  page: 1,
  pageSize: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
  totalItems: 0,
  totalPages: 0,
};

// ============================================================================
// Helper Functions
// ============================================================================

function transformDbPhone(dbPhone: any, dbGroup: any): PhoneNumber {
  return {
    id: dbPhone.id,
    number: dbPhone.number,
    formattedNumber: dbPhone.formatted_number,
    state: dbPhone.state,
    operatingCompanyName: dbPhone.operating_company_name,
    operatingCompanyType: dbPhone.operating_company_type,
    lineType: dbPhone.line_type,
    callGroup: dbGroup?.name || null,
    cnam: dbPhone.cnam,
    description: dbPhone.description,
    department: dbPhone.department,
    internalId: dbPhone.internal_id,
    flagStatus: dbPhone.flag_status as FlagStatus,
    lastFlaggedDate: dbPhone.last_flagged_date,
    lastCheckedDate: dbPhone.last_checked_date,
    reputationSources: dbPhone.reputation_sources || [],
    reputationScore: dbPhone.reputation_score,
    companyId: dbPhone.company_id,
    createdAt: dbPhone.created_at,
    updatedAt: dbPhone.updated_at,
    isArchived: dbPhone.is_archived,
  };
}

function transformDbGroup(dbGroup: any): CallGroup {
  return {
    id: dbGroup.id,
    name: dbGroup.name,
    description: dbGroup.description,
    color: dbGroup.color,
    companyId: dbGroup.company_id,
    phoneCount: dbGroup.phone_count || 0,
    enabledReputationSources: dbGroup.enabled_reputation_sources || [],
    createdAt: dbGroup.created_at,
    updatedAt: dbGroup.updated_at,
  };
}

function transformDbActivity(dbActivity: any): PhoneActivity {
  return {
    id: dbActivity.id,
    phoneId: dbActivity.phone_id,
    type: dbActivity.activity_type,
    source: dbActivity.source,
    date: dbActivity.created_at,
    details: dbActivity.details,
    metadata: dbActivity.metadata || {},
  };
}

function transformDbCheckResult(dbResult: any): PhoneCheckResult {
  return {
    id: dbResult.id,
    phoneId: dbResult.phone_id,
    source: dbResult.source,
    status: dbResult.status,
    confidence: dbResult.confidence,
    checkedAt: dbResult.checked_at,
    rawResponse: dbResult.raw_response || {},
  };
}

function filterPhones(phones: PhoneNumber[], filters: PhoneFilters): PhoneNumber[] {
  Logger.debug(LogCategory.DATA, 'Filtering phones', 'filterPhones', { filters });

  return phones.filter(phone => {
    // Exclude archived phones by default
    if (phone.isArchived) return false;

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch =
        phone.number.toLowerCase().includes(searchLower) ||
        phone.formattedNumber.toLowerCase().includes(searchLower) ||
        phone.cnam?.toLowerCase().includes(searchLower) ||
        phone.description?.toLowerCase().includes(searchLower) ||
        phone.internalId?.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;
    }

    // Status filter
    if (filters.status !== 'all' && phone.flagStatus !== filters.status) {
      return false;
    }

    // Phone number group filter
    if (filters.phoneNumberGroup !== 'all') {
      if (filters.phoneNumberGroup === 'unassigned' && phone.callGroup !== null) {
        return false;
      } else if (filters.phoneNumberGroup !== 'unassigned' && phone.callGroup !== filters.phoneNumberGroup) {
        return false;
      }
    }

    // Date range filter
    if (filters.dateRange.start || filters.dateRange.end) {
      const phoneDate = phone.lastFlaggedDate ? new Date(phone.lastFlaggedDate) : null;

      if (!phoneDate) return false;

      if (filters.dateRange.start) {
        const startDate = new Date(filters.dateRange.start);
        if (phoneDate < startDate) return false;
      }

      if (filters.dateRange.end) {
        const endDate = new Date(filters.dateRange.end);
        if (phoneDate > endDate) return false;
      }
    }

    return true;
  });
}

function sortPhones(phones: PhoneNumber[], config: PhoneSortConfig): PhoneNumber[] {
  Logger.debug(LogCategory.DATA, 'Sorting phones', 'sortPhones', { config });

  return [...phones].sort((a, b) => {
    let comparison = 0;

    switch (config.field) {
      case 'number':
        comparison = a.number.localeCompare(b.number);
        break;
      case 'reputationScore':
        comparison = a.reputationScore - b.reputationScore;
        break;
      case 'lastFlaggedDate': {
        const dateA = a.lastFlaggedDate ? new Date(a.lastFlaggedDate).getTime() : 0;
        const dateB = b.lastFlaggedDate ? new Date(b.lastFlaggedDate).getTime() : 0;
        comparison = dateA - dateB;
        break;
      }
      case 'createdAt': {
        const createdA = new Date(a.createdAt).getTime();
        const createdB = new Date(b.createdAt).getTime();
        comparison = createdA - createdB;
        break;
      }
    }

    return config.direction === 'asc' ? comparison : -comparison;
  });
}

function paginatePhones(
  phones: PhoneNumber[],
  page: number,
  pageSize: number
): { items: PhoneNumber[]; pagination: PaginationState } {
  const totalItems = phones.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (page - 1) * pageSize;
  const items = phones.slice(startIndex, startIndex + pageSize);

  Logger.debug(LogCategory.DATA, 'Paginating phones', 'paginatePhones', {
    page,
    pageSize,
    totalItems,
    totalPages,
    returnedItems: items.length,
  });

  return {
    items,
    pagination: {
      page,
      pageSize,
      totalItems,
      totalPages,
    },
  };
}

// ============================================================================
// Store Implementation
// ============================================================================

export const usePhoneStore = create<PhoneState>()(
  devtools(
    (set, get) => ({
      // Initial State
      phones: [],
      selectedPhone: null,
      selectedPhoneActivities: [],
      selectedPhoneCheckResults: [],
      selectedPhoneDeviceCloud: [],
      callGroups: [],
      filters: defaultFilters,
      sortConfig: defaultSortConfig,
      pagination: defaultPagination,
      isLoading: false,
      isLoadingDetails: false,
      isSaving: false,
      error: null,
      selectedPhoneIds: [],
      realtimeChannel: null,

      // Fetch all phones
      fetchPhones: async () => {
        Logger.info(LogCategory.DATA, 'Fetching phone numbers from Supabase', 'usePhoneStore.fetchPhones');
        Logger.startTimer('fetchPhones');

        set({ isLoading: true, error: null });

        const result = await tryCatch(async () => {
          // Get current session
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            throw new AppError({
              code: ErrorCode.AUTH_SESSION_EXPIRED,
              message: 'No active session',
              userMessage: 'Please log in to view phone numbers.',
              recoverable: true,
              retryable: false,
            });
          }

          // Get user's company
          const { data: user, error: userError } = await supabase
            .from('users')
            .select('company_id')
            .eq('id', session.user.id)
            .single();

          if (userError) throw userError;
          if (!user) throw new Error('User not found');

          // Fetch phones with groups
          const { data: dbPhones, error: phonesError } = await supabase
            .from('phone_numbers')
            .select(`
              *,
              phone_number_groups(id, name, color)
            `)
            .eq('company_id', (user as any).company_id)
            .eq('is_archived', false)
            .order('created_at', { ascending: false });

          if (phonesError) throw phonesError;

          // Transform to match existing types
          const phones = (dbPhones || []).map((dbPhone: any) =>
            transformDbPhone(dbPhone, dbPhone.phone_number_groups)
          );

          return { phones, companyId: (user as any).company_id };
        }, 'fetchPhones');

        Logger.endTimer('fetchPhones');

        if (result.success) {
          const { phones, companyId } = result.data;
          const { filters, sortConfig, pagination } = get();

          // Apply filters and sorting
          const filteredPhones = filterPhones(phones, filters);
          const sortedPhones = sortPhones(filteredPhones, sortConfig);
          const { pagination: newPagination } = paginatePhones(sortedPhones, pagination.page, pagination.pageSize);

          set({
            phones,
            pagination: newPagination,
            isLoading: false,
          });

          // Set up real-time subscription if not already subscribed
          if (!get().realtimeChannel) {
            get().subscribeToChanges(companyId);
          }

          logDataOperation('fetch', 'phones', phones.length);
          logStateChange('phoneStore', 'fetchPhones.success', undefined, {
            phoneCount: phones.length,
          });
        } else {
          set({
            isLoading: false,
            error: result.error.userMessage,
          });

          logDataOperation('fetch', 'phones', 0, undefined, result.error);
        }
      },

      // Fetch call groups
      fetchCallGroups: async () => {
        Logger.info(LogCategory.DATA, 'Fetching call groups from Supabase', 'usePhoneStore.fetchCallGroups');

        const result = await tryCatch(async () => {
          // First check if we have a session
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();

          if (sessionError) {
            Logger.error(LogCategory.DATA, 'Session error', 'fetchCallGroups', undefined, sessionError);
            throw sessionError;
          }

          if (!session) {
            Logger.error(LogCategory.DATA, 'No session found', 'fetchCallGroups');
            throw new Error('Not authenticated - no session');
          }

          Logger.info(LogCategory.DATA, 'Session found', 'fetchCallGroups', { userId: session.user.id });

          // Fetch user with company_id using the session user ID
          const { data: user, error: userError } = await supabase
            .from('users')
            .select('company_id')
            .eq('id', session.user.id)
            .single();

          if (userError) {
            Logger.error(LogCategory.DATA, 'User fetch error', 'fetchCallGroups', undefined, userError);
            throw userError;
          }

          if (!user) {
            Logger.error(LogCategory.DATA, 'No user found', 'fetchCallGroups', { userId: session.user.id });
            throw new Error('User not found in database');
          }

          Logger.info(LogCategory.DATA, 'User found', 'fetchCallGroups', { companyId: (user as any)?.company_id });

          // Fetch groups - simplified without phone count for now
          const { data: dbGroups, error: groupsError } = await supabase
            .from('phone_number_groups')
            .select('*')
            .eq('company_id', (user as any)?.company_id)
            .order('created_at');

          if (groupsError) {
            Logger.error(LogCategory.DATA, 'Error fetching groups', 'fetchCallGroups', undefined, groupsError);
            throw groupsError;
          }

          Logger.info(LogCategory.DATA, `Fetched ${(dbGroups || []).length} groups`, 'fetchCallGroups');

          const groups = (dbGroups || []).map(transformDbGroup);
          return groups;
        }, 'fetchCallGroups');

        if (result.success) {
          set({ callGroups: result.data });
          logDataOperation('fetch', 'callGroups', result.data.length);
          Logger.info(LogCategory.DATA, `Successfully set ${result.data.length} groups in store`, 'fetchCallGroups');
        } else {
          Logger.error(LogCategory.DATA, 'Failed to fetch call groups', 'fetchCallGroups', undefined, result.error);
          console.error('fetchCallGroups full error:', result.error);
          // Set empty array so UI doesn't break
          set({ callGroups: [] });
        }
      },

      // Fetch phone details
      fetchPhoneDetails: async (id: string) => {
        Logger.info(LogCategory.DATA, `Fetching phone details: ${id}`, 'usePhoneStore.fetchPhoneDetails');
        Logger.startTimer(`fetchPhoneDetails_${id}`);

        set({ isLoadingDetails: true, error: null });

        const result = await tryCatch(async () => {
          // Fetch phone with group
          const { data: dbPhone, error: phoneError } = await supabase
            .from('phone_numbers')
            .select(`
              *,
              phone_number_groups(id, name, color)
            `)
            .eq('id', id)
            .single();

          if (phoneError) throw phoneError;
          if (!dbPhone) {
            throw new AppError({
              code: ErrorCode.DATA_NOT_FOUND,
              message: `Phone not found: ${id}`,
              userMessage: 'The requested phone number was not found.',
              recoverable: true,
              retryable: false,
            });
          }

          const phone = transformDbPhone(dbPhone as any, (dbPhone as any).phone_number_groups);

          // Fetch activities
          const { data: dbActivities, error: activitiesError } = await supabase
            .from('phone_activity')
            .select('*')
            .eq('phone_id', id)
            .order('created_at', { ascending: false })
            .limit(50);

          if (activitiesError) throw activitiesError;

          const activities = (dbActivities || []).map(transformDbActivity);

          // Fetch check results
          const { data: dbCheckResults, error: checkResultsError } = await supabase
            .from('phone_check_results')
            .select('*')
            .eq('phone_id', id)
            .order('checked_at', { ascending: false })
            .limit(50);

          if (checkResultsError) throw checkResultsError;

          const checkResults = (dbCheckResults || []).map(transformDbCheckResult);

          // Device cloud results - placeholder for now
          const deviceCloud: DeviceCloudResult[] = [];

          return { phone, activities, checkResults, deviceCloud };
        }, 'fetchPhoneDetails');

        Logger.endTimer(`fetchPhoneDetails_${id}`);

        if (result.success) {
          const { phone, activities, checkResults, deviceCloud } = result.data;

          set({
            selectedPhone: phone,
            selectedPhoneActivities: activities,
            selectedPhoneCheckResults: checkResults,
            selectedPhoneDeviceCloud: deviceCloud,
            isLoadingDetails: false,
          });

          logDataOperation('fetch', 'phoneDetails', 1, { phoneId: id });
        } else {
          set({
            isLoadingDetails: false,
            error: result.error.userMessage,
          });

          logDataOperation('fetch', 'phoneDetails', 0, { phoneId: id }, result.error);
        }
      },

      // Update a phone
      updatePhone: async (id: string, updates: Partial<PhoneNumber>) => {
        Logger.info(LogCategory.DATA, `Updating phone: ${id}`, 'usePhoneStore.updatePhone', {
          updatedFields: Object.keys(updates),
        });

        set({ isSaving: true, error: null });

        const result = await tryCatch(async () => {
          // Transform camelCase to snake_case for database
          const dbUpdates: any = {
            updated_at: new Date().toISOString(),
          };

          if (updates.cnam !== undefined) dbUpdates.cnam = updates.cnam;
          if (updates.description !== undefined) dbUpdates.description = updates.description;
          if (updates.department !== undefined) dbUpdates.department = updates.department;
          if (updates.internalId !== undefined) dbUpdates.internal_id = updates.internalId;
          if (updates.isArchived !== undefined) dbUpdates.is_archived = updates.isArchived;

          // Handle callGroup -> phone_number_group_id
          if (updates.callGroup !== undefined) {
            if (updates.callGroup === null) {
              dbUpdates.phone_number_group_id = null;
            } else {
              // Get group ID by name
              const { data: { session } } = await supabase.auth.getSession();
              if (!session) throw new Error('Not authenticated');

              const { data: user } = await supabase
                .from('users')
                .select('company_id')
                .eq('id', session.user.id)
                .single();

              const { data: group } = await supabase
                .from('phone_number_groups')
                .select('id')
                .eq('company_id', (user as any)?.company_id)
                .eq('name', updates.callGroup)
                .single();

              if (group) {
                dbUpdates.phone_number_group_id = (group as any).id;
              }
            }
          }

          const _dbUpdates: any = dbUpdates;
          // @ts-ignore
          const { data: updatedPhone, error: updateError } = await supabase.from('phone_numbers').update(_dbUpdates).eq('id', id).select(`
              *,
              phone_number_groups(id, name, color)
            `).single();

          if (updateError) throw updateError;
          if (!updatedPhone) throw new Error('Update failed');

          return transformDbPhone(updatedPhone as any, (updatedPhone as any).phone_number_groups);
        }, 'updatePhone');

        if (result.success) {
          const updatedPhone = result.data;
          const { phones, selectedPhone } = get();

          // Update phones array
          const updatedPhones = phones.map(p => (p.id === id ? updatedPhone : p));

          set({
            phones: updatedPhones,
            selectedPhone: selectedPhone?.id === id ? updatedPhone : selectedPhone,
            isSaving: false,
          });

          logDataOperation('update', 'phone', 1, { phoneId: id, updates });
          logStateChange('phoneStore', 'updatePhone.success', undefined, { phoneId: id });
        } else {
          set({
            isSaving: false,
            error: result.error.userMessage,
          });

          logDataOperation('update', 'phone', 0, { phoneId: id }, result.error);
        }
      },

      // Bulk update phones
      bulkUpdatePhones: async (ids: string[], updates: Partial<PhoneNumber>) => {
        Logger.info(LogCategory.DATA, `Bulk updating ${ids.length} phones`, 'usePhoneStore.bulkUpdatePhones', {
          count: ids.length,
          updatedFields: Object.keys(updates),
        });

        set({ isSaving: true, error: null });

        const result = await tryCatch(async () => {
          // Transform updates to database format
          const dbUpdates: any = {
            updated_at: new Date().toISOString(),
          };

          if (updates.isArchived !== undefined) dbUpdates.is_archived = updates.isArchived;

          // Handle callGroup assignment
          if (updates.callGroup !== undefined) {
            if (updates.callGroup === null) {
              dbUpdates.phone_number_group_id = null;
            } else {
              const { data: { session } } = await supabase.auth.getSession();
              if (!session) throw new Error('Not authenticated');

              const { data: user } = await supabase
                .from('users')
                .select('company_id')
                .eq('id', session.user.id)
                .single();

              const { data: group } = await supabase
                .from('phone_number_groups')
                .select('id')
                .eq('company_id', (user as any)?.company_id)
                .eq('name', updates.callGroup)
                .single();

              if (group) {
                dbUpdates.phone_number_group_id = (group as any).id;
              }
            }
          }

          const _dbUpdates: any = dbUpdates;
          // @ts-ignore
          const { data: updatedPhones, error: updateError } = await supabase.from('phone_numbers').update(_dbUpdates).in('id', ids).select(`
              *,
              phone_number_groups(id, name, color)
            `);

          if (updateError) throw updateError;

          return (updatedPhones || []).map((dbPhone: any) =>
            transformDbPhone(dbPhone, dbPhone.phone_number_groups)
          );
        }, 'bulkUpdatePhones');

        if (result.success) {
          const updatedPhones = result.data;
          const { phones } = get();

          const updatedPhonesMap = new Map(updatedPhones.map((p: any) => [p.id, p]));
          const newPhones = phones.map((p: any) => updatedPhonesMap.get(p.id) || p);

          set({
            phones: newPhones as any,
            isSaving: false,
            selectedPhoneIds: [],
          });

          logDataOperation('update', 'phones', updatedPhones.length, { ids, updates });
        } else {
          set({
            isSaving: false,
            error: result.error.userMessage,
          });

          logDataOperation('update', 'phones', 0, { ids }, result.error);
        }
      },

      // Add phones (from CSV upload)
      addPhones: async (newPhones: Partial<PhoneNumber>[]) => {
        Logger.info(LogCategory.DATA, `Adding ${newPhones.length} phones`, 'usePhoneStore.addPhones');

        set({ isSaving: true, error: null });

        const result = await tryCatch(async () => {
          if (newPhones.length === 0) {
            throw new ValidationError(
              'phones',
              'No phones to add',
              'Please provide at least one phone number.',
              ErrorCode.VALIDATION_REQUIRED_FIELD
            );
          }

          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error('Not authenticated');

          const { data: user } = await supabase
            .from('users')
            .select('company_id')
            .eq('id', session.user.id)
            .single();

          if (!user) throw new Error('User not found');

          // Get all group names and fetch their IDs
          const groupNames = [...new Set(newPhones.map(p => p.callGroup).filter(Boolean))];
          const { data: groups } = await supabase
            .from('phone_number_groups')
            .select('id, name')
            .eq('company_id', (user as any).company_id)
            .in('name', groupNames as string[]);

          const groupMap = new Map((groups || []).map((g: any) => [g.name, g.id]));

          // Transform phones to database format
          const dbPhones = newPhones.map(phone => {
            const digits = (phone.number || '').replace(/\D/g, '').slice(-10);
            const formatted = digits.length === 10
              ? `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`
              : phone.number!;
            return {
              company_id: (user as any).company_id,
              phone_number_group_id: phone.callGroup ? groupMap.get(phone.callGroup) : null,
              number: digits || phone.number!,
              formatted_number: phone.formattedNumber || formatted,
              description: phone.description || null,
              internal_id: phone.internalId || null,
              flag_status: 'clean' as const,
              reputation_score: 100,
              reputation_sources: [],
            };
          });

          const { data: insertedPhones, error: insertError } = await supabase
            .from('phone_numbers')
            .insert(dbPhones as any)
            .select();

          if (insertError) throw insertError;

          return insertedPhones || [];
        }, 'addPhones');

        if (result.success) {
          const addedPhones = result.data;

          // Refresh the phones list
          await get().fetchPhones();

          set({ isSaving: false });

          logDataOperation('upload', 'phones', addedPhones.length);
        } else {
          set({
            isSaving: false,
            error: result.error.userMessage,
          });

          logDataOperation('upload', 'phones', 0, undefined, result.error);
        }
      },

      // Archive a phone
      archivePhone: async (id: string) => {
        Logger.info(LogCategory.DATA, `Archiving phone: ${id}`, 'usePhoneStore.archivePhone');
        await get().updatePhone(id, { isArchived: true });
      },

      // Bulk archive phones
      bulkArchivePhones: async (ids: string[]) => {
        Logger.info(LogCategory.DATA, `Bulk archiving ${ids.length} phones`, 'usePhoneStore.bulkArchivePhones');
        await get().bulkUpdatePhones(ids, { isArchived: true });
      },

      // Subscribe to real-time changes
      subscribeToChanges: (companyId: string) => {
        Logger.info(LogCategory.DATA, 'Subscribing to phone number changes', 'subscribeToChanges', { companyId });

        const channel = supabase
          .channel('phone_numbers_changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'phone_numbers',
              filter: `company_id=eq.${companyId}`,
            },
            async (payload) => {
              Logger.debug(LogCategory.DATA, 'Real-time phone update received', 'subscribeToChanges', {
                event: payload.eventType,
              });

              // Refresh phones on any change
              await get().fetchPhones();
            }
          )
          .subscribe();

        set({ realtimeChannel: channel });
      },

      // Unsubscribe from changes
      unsubscribeFromChanges: () => {
        const { realtimeChannel } = get();
        if (realtimeChannel) {
          Logger.info(LogCategory.DATA, 'Unsubscribing from phone number changes', 'unsubscribeFromChanges');
          supabase.removeChannel(realtimeChannel);
          set({ realtimeChannel: null });
        }
      },

      // Set filters
      setFilters: (newFilters: Partial<PhoneFilters>) => {
        Logger.debug(LogCategory.DATA, 'Setting filters', 'usePhoneStore.setFilters', { newFilters });

        const { filters, phones, sortConfig, pagination } = get();
        const updatedFilters = { ...filters, ...newFilters };

        // Apply filters and sorting
        const filteredPhones = filterPhones(phones, updatedFilters);
        const sortedPhones = sortPhones(filteredPhones, sortConfig);
        const { pagination: newPagination } = paginatePhones(sortedPhones, 1, pagination.pageSize);

        set({
          filters: updatedFilters,
          pagination: newPagination,
        });

        logDataOperation('filter', 'phones', filteredPhones.length, { filters: updatedFilters });
      },

      // Reset filters
      resetFilters: () => {
        Logger.debug(LogCategory.DATA, 'Resetting filters', 'usePhoneStore.resetFilters');

        const { phones, sortConfig, pagination } = get();
        const filteredPhones = filterPhones(phones, defaultFilters);
        const sortedPhones = sortPhones(filteredPhones, sortConfig);
        const { pagination: newPagination } = paginatePhones(sortedPhones, 1, pagination.pageSize);

        set({
          filters: defaultFilters,
          pagination: newPagination,
        });
      },

      // Set sort config
      setSortConfig: (config: PhoneSortConfig) => {
        Logger.debug(LogCategory.DATA, 'Setting sort config', 'usePhoneStore.setSortConfig', { config });

        const { phones, filters, pagination } = get();
        const filteredPhones = filterPhones(phones, filters);
        const sortedPhones = sortPhones(filteredPhones, config);
        const { pagination: newPagination } = paginatePhones(sortedPhones, pagination.page, pagination.pageSize);

        set({
          sortConfig: config,
          pagination: newPagination,
        });

        logDataOperation('sort', 'phones', sortedPhones.length, { sortConfig: config });
      },

      // Set page
      setPage: (page: number) => {
        Logger.debug(LogCategory.DATA, `Setting page: ${page}`, 'usePhoneStore.setPage');

        const { phones, filters, sortConfig, pagination } = get();
        const filteredPhones = filterPhones(phones, filters);
        const sortedPhones = sortPhones(filteredPhones, sortConfig);
        const { pagination: newPagination } = paginatePhones(sortedPhones, page, pagination.pageSize);

        set({ pagination: newPagination });

        logDataOperation('paginate', 'phones', undefined, { page, pageSize: pagination.pageSize });
      },

      // Set page size
      setPageSize: (pageSize: number) => {
        Logger.debug(LogCategory.DATA, `Setting page size: ${pageSize}`, 'usePhoneStore.setPageSize');

        const { phones, filters, sortConfig } = get();
        const filteredPhones = filterPhones(phones, filters);
        const sortedPhones = sortPhones(filteredPhones, sortConfig);
        const { pagination: newPagination } = paginatePhones(sortedPhones, 1, pageSize);

        set({ pagination: newPagination });

        logDataOperation('paginate', 'phones', undefined, { page: 1, pageSize });
      },

      // Selection actions
      selectPhone: (id: string) => {
        Logger.debug(LogCategory.DATA, `Selecting phone: ${id}`, 'usePhoneStore.selectPhone');
        const { selectedPhoneIds } = get();
        if (!selectedPhoneIds.includes(id)) {
          set({ selectedPhoneIds: [...selectedPhoneIds, id] });
        }
      },

      deselectPhone: (id: string) => {
        Logger.debug(LogCategory.DATA, `Deselecting phone: ${id}`, 'usePhoneStore.deselectPhone');
        const { selectedPhoneIds } = get();
        set({ selectedPhoneIds: selectedPhoneIds.filter(pid => pid !== id) });
      },

      selectAllPhones: () => {
        Logger.debug(LogCategory.DATA, 'Selecting all phones', 'usePhoneStore.selectAllPhones');
        const paginatedPhones = get().getPaginatedPhones();
        set({ selectedPhoneIds: paginatedPhones.map(p => p.id) });
      },

      deselectAllPhones: () => {
        Logger.debug(LogCategory.DATA, 'Deselecting all phones', 'usePhoneStore.deselectAllPhones');
        set({ selectedPhoneIds: [] });
      },

      togglePhoneSelection: (id: string) => {
        const { selectedPhoneIds } = get();
        if (selectedPhoneIds.includes(id)) {
          get().deselectPhone(id);
        } else {
          get().selectPhone(id);
        }
      },

      // Utility actions
      clearError: () => {
        Logger.debug(LogCategory.DATA, 'Clearing error', 'usePhoneStore.clearError');
        set({ error: null });
      },

      clearSelectedPhone: () => {
        Logger.debug(LogCategory.DATA, 'Clearing selected phone', 'usePhoneStore.clearSelectedPhone');
        set({
          selectedPhone: null,
          selectedPhoneActivities: [],
          selectedPhoneCheckResults: [],
          selectedPhoneDeviceCloud: [],
        });
      },

      // Get filtered phones (for external use)
      getFilteredPhones: () => {
        const { phones, filters, sortConfig } = get();
        const filteredPhones = filterPhones(phones, filters);
        return sortPhones(filteredPhones, sortConfig);
      },

      // Get paginated phones (for display)
      getPaginatedPhones: () => {
        const { phones, filters, sortConfig, pagination } = get();
        const filteredPhones = filterPhones(phones, filters);
        const sortedPhones = sortPhones(filteredPhones, sortConfig);
        const { items } = paginatePhones(sortedPhones, pagination.page, pagination.pageSize);
        return items;
      },
    }),
    { name: 'phoneStore' }
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

export const usePhones = () => usePhoneStore((state) => state.phones);
export const useSelectedPhone = () => usePhoneStore((state) => state.selectedPhone);
export const useSelectedPhoneActivities = () => usePhoneStore((state) => state.selectedPhoneActivities);
export const usePhoneFilters = () => usePhoneStore((state) => state.filters);
export const usePhonePagination = () => usePhoneStore((state) => state.pagination);
export const usePhoneLoading = () => usePhoneStore((state) => state.isLoading);
export const usePhoneError = () => usePhoneStore((state) => state.error);
export const useSelectedPhoneIds = () => usePhoneStore((state) => state.selectedPhoneIds);
export const useCallGroups = () => usePhoneStore((state) => state.callGroups);
export const usePhoneSortConfig = () => usePhoneStore((state) => state.sortConfig);

// Computed selectors - use the store instance for computed values
export const usePaginatedPhones = () => {
  const phones = usePhoneStore((state) => state.phones);
  const filters = usePhoneStore((state) => state.filters);
  const sortConfig = usePhoneStore((state) => state.sortConfig);
  const pagination = usePhoneStore((state) => state.pagination);

  // Apply filters
  const filteredPhones = phones.filter(phone => {
    if (phone.isArchived) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch =
        phone.number.toLowerCase().includes(searchLower) ||
        phone.formattedNumber.toLowerCase().includes(searchLower) ||
        phone.cnam?.toLowerCase().includes(searchLower) ||
        phone.description?.toLowerCase().includes(searchLower) ||
        phone.internalId?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    if (filters.status !== 'all' && phone.flagStatus !== filters.status) return false;
    if (filters.phoneNumberGroup !== 'all') {
      if (filters.phoneNumberGroup === 'unassigned' && phone.callGroup !== null) return false;
      else if (filters.phoneNumberGroup !== 'unassigned' && phone.callGroup !== filters.phoneNumberGroup) return false;
    }
    return true;
  });

  // Sort
  const sortedPhones = [...filteredPhones].sort((a, b) => {
    let comparison = 0;
    switch (sortConfig.field) {
      case 'number': comparison = a.number.localeCompare(b.number); break;
      case 'reputationScore': comparison = a.reputationScore - b.reputationScore; break;
      case 'lastFlaggedDate': {
        const dateA = a.lastFlaggedDate ? new Date(a.lastFlaggedDate).getTime() : 0;
        const dateB = b.lastFlaggedDate ? new Date(b.lastFlaggedDate).getTime() : 0;
        comparison = dateA - dateB;
        break;
      }
      case 'createdAt': {
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      }
    }
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  // Paginate
  const startIndex = (pagination.page - 1) * pagination.pageSize;
  return sortedPhones.slice(startIndex, startIndex + pagination.pageSize);
};

export const useFilteredPhonesCount = () => {
  const phones = usePhoneStore((state) => state.phones);
  const filters = usePhoneStore((state) => state.filters);

  return phones.filter(phone => {
    if (phone.isArchived) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch =
        phone.number.toLowerCase().includes(searchLower) ||
        phone.formattedNumber.toLowerCase().includes(searchLower) ||
        phone.cnam?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    if (filters.status !== 'all' && phone.flagStatus !== filters.status) return false;
    return true;
  }).length;
};

export const useFlaggedCount = () =>
  usePhoneStore((state) => state.phones.filter((p) => p.flagStatus === 'flagged').length);

export const useCleanCount = () =>
  usePhoneStore((state) => state.phones.filter((p) => p.flagStatus === 'clean').length);
